import * as React from "react"
import {
  Card,
  CardHeader,
  CardSection,
  Heading,
  Spacer,
  Text,
  ThemeCss,
} from "gatsby-interface"
import { SiteSettingsSection, SiteSettingsSectionLabel } from "../constants"
import { EnablePreviewBuilds } from "./EnablePreviewBuilds"
import { PreviewTriggerType } from "@modules/graphql/types"
import { useSitePreviewSettingsFieldsFragment } from "../fragments.generated"
import { useFlags } from "@modules/featureFlags"
import { CardSeparator } from "@modules/ui/components/Card"
import { getPreviewWebhookUrl } from "@modules/site/shared/helpers"
import CopyUrl from "@modules/ui/components/CopyUrl"
import { sitePreviewSettings as text } from "@modules/locales/default.json"
import { PreviewUpdateTypeConfiguration } from "./PreviewUpdateTypeConfiguration"

const mainSettingsCss: ThemeCss = theme => ({
  display: `grid`,
  gridGap: theme.space[5],
})

const widgetConfigTitleCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[4],
  margin: 0,
})

const webhookTitleCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[3],
  margin: 0,
})

export type PreviewSettingsSectionProps = {
  siteId: string
}

export function PreviewSettingsSection({
  siteId,
}: PreviewSettingsSectionProps) {
  const { data: siteData } = useSitePreviewSettingsFieldsFragment(siteId)
  const { flags } = useFlags()

  if (!siteData) {
    return null
  }

  const previewWebhookUrl = getPreviewWebhookUrl(siteId)

  return (
    <Card>
      <CardHeader
        title={SiteSettingsSectionLabel[SiteSettingsSection.Preview]}
        titleAs="h2"
      />

      <CardSection applyPaddingTop={false} css={mainSettingsCss}>
        <EnablePreviewBuilds
          siteId={siteId}
          previewBuildsEnabled={siteData.previewBuildsEnabled ?? false}
          disabled={typeof siteData.previewBuildsEnabled !== `boolean`}
        />
      </CardSection>
      {flags.cmsWidgetConfiguration && siteData.previewBuildsEnabled && (
        <React.Fragment>
          <CardSeparator />
          <CardSection>
            <Heading css={widgetConfigTitleCss} as="h3">
              {text.headers.configureWidget}
            </Heading>
            <Spacer size={7} />
            <PreviewUpdateTypeConfiguration
              siteId={siteId}
              previewTriggerType={
                siteData.previewTriggerType ?? PreviewTriggerType.Automatic
              }
            />
            <Spacer size={9} />
            <Heading as="h4" css={webhookTitleCss}>
              {text.headers.gatsbyCloudWebhook}
            </Heading>
            <Spacer size={3} />
            <Text id="preview-webhook-description" size="S" css={{ margin: 0 }}>
              {text.messages.gatsbyCloudWebhookDescription}
            </Text>
            <Spacer size={5} />
            <CopyUrl
              url={previewWebhookUrl}
              aria-describedby="preview-webhook-description"
            />
          </CardSection>
        </React.Fragment>
      )}
    </Card>
  )
}
